import React from 'react';
import { Trans } from '@lingui/react/macro';

import { QuickFactHighlight } from '@rover/react-lib/src/pages/landing/components/QuickFact';
import type { PetTypeEnum } from '@rover/rsdk/src/apiClient/latest/model/petTypeEnum';
import { ServiceTypeSlug } from '@rover/types/src/ServiceType';

import { SERVICE_LANDING_QUICK_FACT_STATS as stats } from './serviceLandingQuickFactStats';

export type ServiceLandingFormatNumber = (value: number) => string;
export type ServiceLandingQuickFactText = (format: ServiceLandingFormatNumber) => React.ReactNode;
export type ServiceLandingQuickFactBands = {
  first: ServiceLandingQuickFactText;
  second: ServiceLandingQuickFactText;
};
export type ServiceLandingQuickFactPetKey = PetTypeEnum | 'default';

// The facts of each service type and pet type. Each message copies its stat into a local first
// because Lingui names the placeholder after the variable.
export const DEFAULT_SERVICE_LANDING_QUICK_FACTS: Partial<
  Record<
    ServiceTypeSlug,
    Partial<Record<ServiceLandingQuickFactPetKey, ServiceLandingQuickFactBands>>
  >
> = {
  [ServiceTypeSlug.OVERNIGHT_BOARDING]: {
    dog: {
      first: (format) => {
        const numDogs = format(stats.overnightDogs);

        return (
          <Trans comment="{numDogs} is a spelled-out amount such as '4.7 million'; add the linking word (de, di) before the noun where the language needs it">
            Over <QuickFactHighlight>{numDogs}</QuickFactHighlight> dogs curled up for a sleepover
            in a sitter's home on Rover.
          </Trans>
        );
      },
      second: (format) => {
        const sittersPct = format(stats.overnightDogSittersPct);

        return (
          <Trans>
            More than <QuickFactHighlight>{sittersPct}%</QuickFactHighlight> of dog boarding sitters
            have over 4.9 star reviews.
          </Trans>
        );
      },
    },
    cat: {
      first: (format) => {
        const numCats = format(stats.overnightCats);

        return (
          <Trans comment="{numCats} is a spelled-out amount such as '4.7 million'; add the linking word (de, di) before the noun where the language needs it">
            Over <QuickFactHighlight>{numCats}</QuickFactHighlight> cats settled into a sitter's
            home for a sleepover on Rover.
          </Trans>
        );
      },
      second: (format) => {
        const sittersPct = format(stats.overnightCatSittersPct);

        return (
          <Trans>
            More than <QuickFactHighlight>{sittersPct}%</QuickFactHighlight> of cat boarding sitters
            have over 4.9 star reviews.
          </Trans>
        );
      },
    },
    default: {
      first: (format) => {
        const numPets = format(stats.overnightPets);

        return (
          <Trans comment="{numPets} is a spelled-out amount such as '4.7 million'; add the linking word (de, di) before the noun where the language needs it">
            Over <QuickFactHighlight>{numPets}</QuickFactHighlight> pets have boarded in a sitter's
            home on Rover.
          </Trans>
        );
      },
      second: (format) => {
        const sittersPct = format(stats.overnightPetSittersPct);

        return (
          <Trans>
            More than <QuickFactHighlight>{sittersPct}%</QuickFactHighlight> of pet boarding sitters
            have over 4.9 star reviews.
          </Trans>
        );
      },
    },
  },
  [ServiceTypeSlug.DROP_IN]: {
    dog: {
      first: (format) => {
        const numDogs = format(stats.dropInDogs);

        return (
          <Trans comment="{numDogs} is a spelled-out amount such as '4.7 million'; add the linking word (de, di) before the noun where the language needs it">
            Over <QuickFactHighlight>{numDogs}</QuickFactHighlight> pups enjoyed cuddles and care
            from sitters on Rover.
          </Trans>
        );
      },
      second: (format) => {
        const numParents = format(stats.dropInDogParents);

        return (
          <Trans>
            More than <QuickFactHighlight>{numParents}</QuickFactHighlight> dog parents have given
            their sitter 5-stars.
          </Trans>
        );
      },
    },
    cat: {
      first: (format) => {
        const numCats = format(stats.dropInCats);

        return (
          <Trans comment="{numCats} is a spelled-out amount such as '4.7 million'; add the linking word (de, di) before the noun where the language needs it">
            Over <QuickFactHighlight>{numCats}</QuickFactHighlight> cats received care visits from
            their favorite sitter.
          </Trans>
        );
      },
      second: (format) => {
        const numParents = format(stats.dropInCatParents);

        return (
          <Trans>
            More than <QuickFactHighlight>{numParents}</QuickFactHighlight> cat parents have given
            their sitter 5-stars.
          </Trans>
        );
      },
    },
    default: {
      first: (format) => {
        const numPets = format(stats.dropInPets);

        return (
          <Trans comment="{numPets} is a spelled-out amount such as '4.7 million'; add the linking word (de, di) before the noun where the language needs it">
            Over <QuickFactHighlight>{numPets}</QuickFactHighlight> pets got personalized attention
            and care during a Drop-In visit.
          </Trans>
        );
      },
      second: (format) => {
        const numParents = format(stats.dropInPetParents);

        return (
          <Trans comment="{numParents} is a spelled-out amount such as '4.7 million'; add the linking word (de, di) before the noun where the language needs it">
            More than <QuickFactHighlight>{numParents}</QuickFactHighlight> pet parents have given
            their sitter 5-stars.
          </Trans>
        );
      },
    },
  },
  [ServiceTypeSlug.OVERNIGHT_TRAVELING]: {
    default: {
      first: (format) => {
        const numPets = format(stats.houseSittingPets);

        return (
          <Trans comment="{numPets} is a spelled-out amount such as '4.7 million'; add the linking word (de, di) before the noun where the language needs it">
            Over <QuickFactHighlight>{numPets}</QuickFactHighlight> pets got a comfy staycation at
            home.
          </Trans>
        );
      },
      second: (format) => {
        const sittersPct = format(stats.houseSittingSittersPct);

        return (
          <Trans>
            <QuickFactHighlight>{sittersPct}%</QuickFactHighlight> of house sitters with pet
            experience on Rover have over 4.9 star reviews.
          </Trans>
        );
      },
    },
  },
  [ServiceTypeSlug.DOG_TRAINING]: {
    dog: {
      first: (format) => {
        const numDogs = format(stats.trainingDogs);

        return (
          <Trans>
            Over <QuickFactHighlight>{numDogs}</QuickFactHighlight> dogs (and their humans!) leveled
            up their skills, thanks to dog trainers on Rover.
          </Trans>
        );
      },
      second: (format) => {
        const numTrainers = format(stats.trainingTrainers);

        return (
          <Trans>
            <QuickFactHighlight>{numTrainers}</QuickFactHighlight> trainers are available to work
            with your dog.
          </Trans>
        );
      },
    },
  },
  [ServiceTypeSlug.DOGGY_DAY_CARE]: {
    dog: {
      first: (format) => {
        const numDogs = format(stats.dayCareDogs);

        return (
          <Trans comment="{numDogs} is a spelled-out amount such as '4.7 million'; add the linking word (de, di) before the noun where the language needs it">
            Over <QuickFactHighlight>{numDogs}</QuickFactHighlight> dogs traded couch time for play
            time with Doggy Day Care on Rover.
          </Trans>
        );
      },
      second: (format) => {
        const numSpots = format(stats.dayCareSpots);

        return (
          <Trans comment="{numSpots} is a spelled-out amount such as '4.7 million'; add the linking word (de, di) before the noun where the language needs it">
            <QuickFactHighlight>{numSpots}</QuickFactHighlight> doggy day care spots are available.
          </Trans>
        );
      },
    },
  },
  [ServiceTypeSlug.DOG_WALKING]: {
    dog: {
      first: (format) => {
        const numDogs = format(stats.walkingDogs);

        return (
          <Trans comment="{numDogs} is a spelled-out amount such as '4.7 million'; add the linking word (de, di) before the noun where the language needs it">
            Over <QuickFactHighlight>{numDogs}</QuickFactHighlight> dogs enjoyed local sniffing
            adventures with dog walkers.
          </Trans>
        );
      },
      second: (format) => {
        const walkersPct = format(stats.walkingSameWalkerPct);

        return (
          <Trans>
            More than <QuickFactHighlight>{walkersPct}%</QuickFactHighlight> of dogs get their
            weekly exercise with the same dog walker on Rover.
          </Trans>
        );
      },
    },
  },
};
